// src/app/modules/Devices/pages/DeviceDetail.jsx
import React, { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Fingerprint, AlertTriangle } from 'lucide-react';
import { devicesApi, errorMessage } from '../api';
import { useDeviceAccess, usePolling, useEvent, lastSeen, dateTime, signal, HEALTH_FLAGS } from '../utils';
import { Pill, Fact, Button, Spinner, EmptyState } from '../components/ui';
import { EnrolModal } from '../components/EnrolModal';
import { FingerprintsTab } from '../components/FingerprintsTab';
import { ScansTab } from '../components/ScansTab';
import { CommandsTab } from '../components/CommandsTab';
import { SettingsTab } from '../components/SettingsTab';
import { DiagnosticsTab } from '../components/DiagnosticsTab';
import '../devices.scss';

const REFRESH_MS = 6000;

const TABS = [
  { key: 'fingerprints', label: 'Fingerprints', count: (d) => d.stats?.enrolled_fingerprints },
  { key: 'scans', label: 'Scans', count: (d) => d.stats?.events_needing_review, countTone: 'danger' },
  { key: 'commands', label: 'Commands', count: (d) => d.stats?.pending_commands },
  { key: 'settings', label: 'Settings' },
  { key: 'diagnostics', label: 'Diagnostics' },
];

export function DeviceDetail() {
  const { deviceId } = useParams();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get('tab')) ? params.get('tab') : 'fingerprints';
  const { orgId, canManage, locations } = useDeviceAccess();
  const [device, setDevice] = useState(null);
  const [error, setError] = useState(null);
  const [enrolOpen, setEnrolOpen] = useState(false);

  const load = useEvent(async () => {
    if (!orgId) return;
    try {
      setDevice(await devicesApi.get(orgId, deviceId));
      setError(null);
    } catch (err) {
      setError(err?.response?.status === 404 ? 'not_found' : errorMessage(err, 'Could not load device'));
    }
  });
  usePolling(load, REFRESH_MS, !!orgId);

  if (error === 'not_found') {
    return (
      <div className="dv-page">
        <Link to="/devices" className="dv-back"><ChevronLeft size={14} />All devices</Link>
        <div className="dv-card"><EmptyState icon={Fingerprint} title="Device not found">It may have been moved to another organization.</EmptyState></div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="dv-page">
        <Link to="/devices" className="dv-back"><ChevronLeft size={14} />All devices</Link>
        {error ? <div className="dv-notice dv-notice--danger"><AlertTriangle size={16} />{error}</div> : <div style={{ textAlign: 'center', padding: 40 }}><Spinner size={20} /></div>}
      </div>
    );
  }

  const hb = device.last_heartbeat || {};
  const rssi = signal(hb.wifi_rssi);
  const flags = (device.health_flags || []).map((f) => HEALTH_FLAGS[f]).filter(Boolean);
  const tabProps = { orgId, device, canManage, onChanged: load, onSaved: load };

  return (
    <div className="dv-page">
      <div>
        <Link to="/devices" className="dv-back"><ChevronLeft size={14} />All devices</Link>
        <div className="dv-header">
          <div className="dv-hero__title">
            <span className="dv-device__icon"><Fingerprint size={20} /></span>
            <div>
              <h1>{device.name || device.device_id}</h1>
              <p style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>{device.device_id}</p>
            </div>
          </div>
          {canManage && device.status === 'active' && (
            <div className="dv-header__actions">
              <Button variant="primary" icon={Fingerprint} onClick={() => setEnrolOpen(true)}>
                {device.registration_open ? 'Enrolment in progress' : 'Enrol a fingerprint'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {device.status !== 'active' && (
        <div className="dv-notice dv-notice--danger">
          <AlertTriangle size={16} />
          <span>This device is <b>{device.status}</b> and can’t send scans.{canManage && ' Re-enable it under Settings.'}</span>
        </div>
      )}

      <div className="dv-card">
        <div className="dv-hero">
          <dl className="dv-facts">
            <Fact label="Location">{device.location_name || '—'}</Fact>
            <Fact label="Last seen"><span title={dateTime(device.last_seen_at)}>{lastSeen(device.last_seen_at)}</span></Fact>
            <Fact label="Firmware">{device.firmware_version || '—'}</Fact>
            <Fact label="Wi-Fi signal"><Pill tone={rssi.tone}>{rssi.label}</Pill></Fact>
            <Fact label="Scans waiting on device">{hb.pending_events != null ? hb.pending_events : '—'}</Fact>
            <Fact label="Last status report"><span title={dateTime(hb.received_at)}>{lastSeen(hb.received_at)}</span></Fact>
          </dl>
          {flags.length > 0 && (
            <div className="dv-hero__flags">{flags.map((f) => <Pill key={f.label} tone={f.tone}>{f.label}</Pill>)}</div>
          )}
          <p style={{ fontSize: 12, color: '#7E8299', margin: 0 }}>
            Devices sleep to save power and only connect when used, so a quiet device in an unused room is normal.
          </p>
        </div>
      </div>

      <div className="dv-card">
        <nav className="dv-tabs" role="tablist">
          {TABS.map((t) => {
            const n = t.count ? t.count(device) : null;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={tab === t.key}
                className={tab === t.key ? 'active' : ''}
                onClick={() => setParams(t.key === 'fingerprints' ? {} : { tab: t.key }, { replace: true })}
              >
                {t.label}
                {n > 0 && <span className="dv-tabs__count" style={t.countTone === 'danger' ? { background: '#FEF2F2', color: '#DC2626' } : undefined}>{n}</span>}
              </button>
            );
          })}
        </nav>

        {tab === 'fingerprints' && <FingerprintsTab {...tabProps} onEnrol={() => setEnrolOpen(true)} />}
        {tab === 'scans' && <ScansTab {...tabProps} />}
        {tab === 'commands' && <CommandsTab {...tabProps} />}
        {tab === 'settings' && <SettingsTab {...tabProps} locations={locations} />}
        {tab === 'diagnostics' && <DiagnosticsTab {...tabProps} />}
      </div>

      {canManage && (
        <EnrolModal
          show={enrolOpen}
          onHide={() => { setEnrolOpen(false); load(); }}
          orgId={orgId}
          device={device}
          onEnrolled={load}
        />
      )}
    </div>
  );
}

export default DeviceDetail;
